import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <section id="categories" className="relative bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            {t("categoriesSection.title")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {t("categoriesSection.subtitle")}
          </p>
        </div>

        <div ref={ref} className="relative mx-auto mt-8 w-full max-w-md">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-lg border border-background-300/70 bg-background-100 px-4 py-3 text-left text-sm font-medium text-foreground-800 transition-colors hover:border-background-400 cursor-pointer"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-list-check-2 text-base"></i>
              </span>
              {t("dropdown.placeholder")}
            </span>
            <span className="flex h-5 w-5 items-center justify-center">
              <i className={`ri-arrow-down-s-line text-base transition-transform ${open ? "rotate-180" : ""}`}></i>
            </span>
          </button>

          {open && (
            <ul
              className="absolute left-0 right-0 z-30 mt-2 max-h-80 overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-1"
              role="listbox"
            >
              {categories.map((cat) => (
                <li key={cat.id}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                    role="option"
                  >
                    <img
                      src={cat.image}
                      alt={t(cat.nameKey)}
                      className="h-9 w-9 flex-shrink-0 rounded-md object-cover object-top"
                    />
                    <span className="flex-1">{t(cat.nameKey)}</span>
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-up-line text-sm"></i>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
            >
              <div className="h-40 w-full overflow-hidden bg-background-100 md:h-44">
                <img
                  src={cat.image}
                  alt={t(cat.nameKey)}
                  title={`${t(cat.nameKey)} — USFans Spreadsheet`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between p-3 md:p-4">
                <span className="text-sm font-semibold text-foreground-900 md:text-base">
                  {t(cat.nameKey)}
                </span>
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}